import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ScoresForEng } from "../Components/Resource/ScoresForEng";
import { TicketListEng } from "../Components/Resource/TicketListEng";
import { SimpleModal } from "../Components/Shared/SimpleModal";
import { Loading } from "../Components/Loading/Loading";
import { redirectToLogin } from "../utils/auth";

function buildStageHelpers(statuses) {
  const activeStages = new Set();
  const aliasMap = {};
  for (const s of statuses) {
    if (s.isActive) activeStages.add(s.key);
    for (const alias of s.aliases || []) aliasMap[alias] = s.key;
  }
  return {
    activeStages,
    normalizeStage: (name) => aliasMap[name] || name,
  };
}

function shortName(email) {
  if (!email) return "Unknown";
  return email.split("@")[0].split(".").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

function fmtDate(iso) {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "\u2014";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function overdueDays(deadline) {
  if (!deadline) return null;
  const dl = new Date(deadline);
  if (isNaN(dl.getTime())) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  dl.setHours(0, 0, 0, 0);
  const diff = Math.floor((now - dl) / 86400000);
  return diff > 0 ? diff : null;
}

function hoursLeft(deadline) {
  if (!deadline) return null;
  const dl = new Date(deadline);
  if (isNaN(dl.getTime())) return null;
  const diff = dl - new Date();
  if (diff < 0) return null;
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(h / 24);
  if (d > 0) return `${d}d ${h % 24}h`;
  return `${h}h`;
}

export const Resource = () => {
  const { ResourceId } = useParams();
  const navigate = useNavigate();
  const authToken = localStorage.getItem("authToken");

  const [allTickets, setAllTickets] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [activeStages, setActiveStages] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("tasks");
  const [filter, setFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [attentionOpen, setAttentionOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const effRef = useRef(null);

  const displayName = ResourceId.split(".").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

  useEffect(() => {
    if (!authToken) {
      redirectToLogin();
      return;
    }

    const BASE = process.env.REACT_APP_BACKEND_URL;
    const headers = { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` };
    const get = (url) =>
      fetch(url, { headers }).then((r) => {
        if (r.status === 401) { navigate("/Dashboard", { replace: true }); return null; }
        return r.json();
      });

    Promise.all([
      get(`${BASE}/API/V2/Tickets`),             // tickets
      get(`${BASE}/API/V2/config/status`),        // statuses
      get(`${BASE}/API/V2/config/priorities`),    // priorities
      get(`${BASE}/API/V2/config/requesttypes`),  // requestOptions
    ])
      .then(([ticketsData, statusData, prioritiesData, requestTypesData]) => {
        if (!ticketsData || !statusData || !prioritiesData || !requestTypesData) return;
        if (ticketsData.error) return;

        const statuses = statusData || [];
        setStatusList(statuses);
        const { activeStages: aStages, normalizeStage } = buildStageHelpers(statuses);
        setActiveStages(aStages);

        const sLookup = {};
        statuses.forEach((s) => { sLookup[s.value] = s.key; });
        const pLookup = {};
        (prioritiesData || []).forEach((p) => { pLookup[p.value] = p.key; });
        const rLookup = {};
        (requestTypesData || []).forEach((r) => { rLookup[r.value] = r.key; });

        const enriched = (ticketsData || [])
          .filter(
            (t) =>
              (t.assign && t.assign.split("@")[0] === ResourceId) ||
              (t.createdBy && t.createdBy.split("@")[0] === ResourceId)
          )
          .map((t) => ({
            ...t,
            stageName: normalizeStage(sLookup[t.status] || `Unknown`),
            priorityName: pLookup[t.priority] || `P${t.priority}`,
            requestTypeName: rLookup[t.requestType] || `Type ${t.requestType}`,
          }));

        setAllTickets(enriched);
        setLoading(false);
      })
      .catch((err) => console.error("Fetch error:", err));
  }, [authToken, ResourceId, navigate]);

  const assigned = useMemo(
    () => allTickets.filter((t) => t.assign && t.assign.split("@")[0] === ResourceId),
    [allTickets, ResourceId]
  );

  const activeInfo = useMemo(() => {
    const active = assigned.filter((t) => activeStages.has(t.stageName));
    if (active.length > 0) return { type: "active", count: active.length };
    const closed = assigned
      .filter((t) => !activeStages.has(t.stageName))
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    if (closed.length > 0) {
      const last = closed[0];
      const d = Math.max(0, Math.floor((new Date() - new Date(last.updatedAt)) / 86400000));
      return { type: "lastClosed", ticketId: last.ticketId, days: d };
    }
    return { type: "none" };
  }, [assigned, activeStages]);

  const { etaCrossed, approaching, critical, attentionCount } = useMemo(() => {
    const now = new Date();
    const active = assigned.filter((t) => activeStages.has(t.stageName));
    const eta = active.filter((t) => {
      if (!t.deadline) return false;
      const dl = new Date(t.deadline);
      return !isNaN(dl.getTime()) && dl < now;
    });
    const appr = active.filter((t) => {
      if (!t.deadline) return false;
      const dl = new Date(t.deadline);
      if (isNaN(dl.getTime())) return false;
      const diff = (dl - now) / 86400000;
      return diff >= 0 && diff <= 2;
    });
    const crit = active.filter((t) => t.priorityName?.toLowerCase() === "critical");
    return { etaCrossed: eta, approaching: appr, critical: crit, attentionCount: eta.length + appr.length + crit.length };
  }, [assigned, activeStages]);

  const stats = useMemo(() => {
    const now = new Date();
    const active = assigned.filter((t) => activeStages.has(t.stageName));
    let od = 0,
      nod = 0;
    for (const t of active) {
      if (!t.deadline) { nod++; continue; }
      const dl = new Date(t.deadline);
      if (isNaN(dl.getTime())) { nod++; continue; }
      dl < now ? od++ : nod++;
    }
    const cutoff = new Date(now.getTime() - 15 * 86400000);
    const recent = assigned.filter((t) => { const d = new Date(t.createdAt); return !isNaN(d.getTime()) && d >= cutoff; });
    const closedRecent = recent.filter((t) => !activeStages.has(t.stageName)).length;
    const rate = recent.length > 0 ? (closedRecent / recent.length) * 100 : 0;
    return { overdue: od, notOverdue: nod, activeCount: active.length, closingRate: rate.toFixed(2) };
  }, [assigned, activeStages]);

  if (loading) return <Loading />;

  const C = 2 * Math.PI * 40;
  const safeLen = stats.activeCount > 0 ? (stats.notOverdue / stats.activeCount) * C : 0;
  const overdueLen = stats.activeCount > 0 ? (stats.overdue / stats.activeCount) * C : 0;

  const attentionTable = (items, headers, rowRender, emptyMsg, color) => (
    <div className="overflow-hidden rounded-lg border border-gray-200">
      <table className="w-full text-xs">
        <thead>
          <tr className={color}>
            {headers.map((h) => (
              <th key={h} className="px-3 py-1.5 text-left text-[10px] font-bold uppercase">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {items.length === 0 ? (
            <tr><td colSpan={headers.length} className="px-3 py-4 text-center text-gray-400">{emptyMsg}</td></tr>
          ) : (
            items.map((t) => rowRender(t))
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="p-2 rounded-3xl mx-6 mt-4 flex flex-col gap-3">
      {/* SECTION 1: Header Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-5 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="text-lg font-black tracking-tight text-gray-900">{displayName}</span>
          {activeInfo.type === "active" && (
            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-jiojbm20 text-jiojbm60">
              Active: {activeInfo.count}
            </span>
          )}
          {activeInfo.type === "lastClosed" && (
            <button
              onClick={() => navigate(`/Ticket/${activeInfo.ticketId}`)}
              className="text-xs font-bold px-2.5 py-1 rounded-full bg-jiojbm20 text-jiojbm60 hover:bg-jiojbm25 transition"
            >
              Last Closed: {activeInfo.ticketId} {activeInfo.days}d ago
            </button>
          )}
          {activeInfo.type === "none" && (
            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-gray-100 text-gray-400">No tickets</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setAttentionOpen(true)}
            className="relative px-3 py-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-700 font-bold text-sm transition"
          >
            Attention
            {attentionCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] font-black px-1">
                {attentionCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setStatsOpen(true)}
            className="px-3 py-1.5 rounded-full bg-jiojbm20 hover:bg-jiojbm25 text-jiojbm60 font-bold text-sm transition"
          >
            Stats
          </button>
        </div>
      </div>

      {/* SECTION 2: Scores Bar */}
      <ScoresForEng
        tickets={allTickets}
        status={statusList}
        mode={mode}
        user={ResourceId}
        filter={filter}
        setFilter={setFilter}
        startDate={startDate}
        endDate={endDate}
      />

      {/* SECTION 3: Ticket List */}
      <TicketListEng
        tickets={allTickets}
        mode={mode}
        setMode={setMode}
        user={ResourceId}
        filter={filter}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
      />

      {/* MODAL 1: Attention Required */}
      <SimpleModal
        open={attentionOpen}
        title="Attention Required"
        onClose={() => setAttentionOpen(false)}
        maxWidth="max-w-4xl"
        heightClass="max-h-[85vh]"
      >
        <div className="flex flex-col gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full bg-red-500" />
              <span className="text-sm font-black text-red-700">ETA Crossed ({etaCrossed.length})</span>
            </div>
            {attentionTable(
              etaCrossed,
              ["Ticket", "Application", "Stage", "Deadline", "Overdue by"],
              (t) => (
                <tr key={t.ticketId} className="hover:bg-red-50/40 cursor-pointer" onClick={() => { setAttentionOpen(false); navigate(`/Ticket/${t.ticketId}`); }}>
                  <td className="px-3 py-1.5 font-bold text-jiojbm60">{t.ticketId}</td>
                  <td className="px-3 py-1.5 text-gray-600">{t.org || "\u2014"}</td>
                  <td className="px-3 py-1.5 text-gray-600">{t.stageName}</td>
                  <td className="px-3 py-1.5 text-gray-500">{fmtDate(t.deadline)}</td>
                  <td className="px-3 py-1.5 text-right font-bold text-red-600">{overdueDays(t.deadline)}d</td>
                </tr>
              ),
              "No overdue tickets.",
              "bg-red-50"
            )}
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full bg-amber-500" />
              <span className="text-sm font-black text-amber-700">Approaching deadline ({approaching.length})</span>
            </div>
            {attentionTable(
              approaching,
              ["Ticket", "Application", "Stage", "Deadline", "Time left"],
              (t) => (
                <tr key={t.ticketId} className="hover:bg-amber-50/40 cursor-pointer" onClick={() => { setAttentionOpen(false); navigate(`/Ticket/${t.ticketId}`); }}>
                  <td className="px-3 py-1.5 font-bold text-jiojbm60">{t.ticketId}</td>
                  <td className="px-3 py-1.5 text-gray-600">{t.org || "\u2014"}</td>
                  <td className="px-3 py-1.5 text-gray-600">{t.stageName}</td>
                  <td className="px-3 py-1.5 text-gray-500">{fmtDate(t.deadline)}</td>
                  <td className="px-3 py-1.5 text-right font-bold text-amber-600">{hoursLeft(t.deadline) || "\u2014"}</td>
                </tr>
              ),
              "No tickets nearing deadline.",
              "bg-amber-50"
            )}
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full bg-jiojbmpurple" />
              <span className="text-sm font-black text-jiojbm60">Critical priority ({critical.length})</span>
            </div>
            {attentionTable(
              critical,
              ["Ticket", "Application", "Stage", "Deadline"],
              (t) => (
                <tr key={t.ticketId} className="hover:bg-jiojbm20/40 cursor-pointer" onClick={() => { setAttentionOpen(false); navigate(`/Ticket/${t.ticketId}`); }}>
                  <td className="px-3 py-1.5 font-bold text-jiojbm60">{t.ticketId}</td>
                  <td className="px-3 py-1.5 text-gray-600">{t.org || "\u2014"}</td>
                  <td className="px-3 py-1.5 text-gray-600">{t.stageName}</td>
                  <td className="px-3 py-1.5 text-gray-500">{fmtDate(t.deadline)}</td>
                </tr>
              ),
              "No critical tickets.",
              "bg-jiojbm20"
            )}
          </div>
        </div>
      </SimpleModal>

      {/* MODAL 2: Stats */}
      <SimpleModal
        open={statsOpen}
        title={`${displayName} \u2014 Stats`}
        onClose={() => setStatsOpen(false)}
        maxWidth="max-w-5xl"
        heightClass="h-[78vh]"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-xl border border-gray-200 bg-white p-3 flex flex-col items-center">
            <h4 className="text-lg font-black self-start mb-2">Overdue Tickets</h4>
            {stats.activeCount === 0 ? (
              <div className="relative flex items-center justify-center w-48 h-48">
                <svg viewBox="0 0 120 120" className="w-48 h-48">
                  <circle cx="60" cy="60" r="40" fill="none" stroke="#e5e7eb" strokeWidth="16" />
                  <text x="60" y="62" textAnchor="middle" fontSize="9" fill="#9ca3af" fontWeight="600">No Open Tickets</text>
                </svg>
              </div>
            ) : (
              <>
                <svg viewBox="0 0 120 120" className="w-48 h-48">
                  <circle cx="60" cy="60" r="40" fill="none" stroke="#22c55e" strokeWidth="16"
                    strokeDasharray={`${safeLen} ${C - safeLen}`}
                    transform="rotate(-90 60 60)" />
                  {overdueLen > 0 && (
                    <circle cx="60" cy="60" r="40" fill="none" stroke="#ef4444" strokeWidth="16"
                      strokeDasharray={`${overdueLen} ${C - overdueLen}`}
                      strokeDashoffset={`${-safeLen}`}
                      transform="rotate(-90 60 60)" />
                  )}
                  <text x="60" y="56" textAnchor="middle" fontSize="22" fontWeight="800" fill="#141414">{stats.activeCount}</text>
                  <text x="60" y="70" textAnchor="middle" fontSize="7" fill="#6b7280" fontWeight="600">ACTIVE TICKETS</text>
                </svg>
                <div className="flex gap-4 mt-1 text-xs">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-green-500" /> Not Overdue ({stats.notOverdue})
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-red-500" /> Overdue ({stats.overdue})
                  </span>
                </div>
              </>
            )}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-3" ref={effRef}>
            <h4 className="text-lg font-black mb-2">Efficiency matrix</h4>
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left font-black border-b border-gray-200 rounded-tl-lg">Metric</th>
                  <th className="px-3 py-2 text-right font-black border-b border-gray-200 rounded-tr-lg">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr className="hover:bg-gray-50">
                  <td className="px-3 py-2.5 text-gray-700">Avg. time in W/P</td>
                  <td className="px-3 py-2.5 text-right font-bold text-gray-800">&mdash;</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-3 py-2.5 text-gray-700">Avg. time in Complete</td>
                  <td className="px-3 py-2.5 text-right font-bold text-gray-800">&mdash;</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-3 py-2.5 text-gray-700">Ticket Closing Rate (15d)</td>
                  <td className="px-3 py-2.5 text-right font-bold text-gray-800">{stats.closingRate}%</td>
                </tr>
              </tbody>
            </table>
            <p className="text-[10px] text-gray-400 mt-2">* Avg. time metrics require ticket history data for precise calculation.</p>
          </div>
        </div>
      </SimpleModal>
    </div>
  );
};
